/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { CancelablePromise } from '../core/CancelablePromise'
import { OpenAPI } from '../core/OpenAPI'
import { request as __request } from '../core/request'
import type { CompanyContactDto } from '../models/CompanyContactDto'
import type { CompanyDuplicateDto } from '../models/CompanyDuplicateDto'
import type { CompanyLocationDto } from '../models/CompanyLocationDto'
import type { CompanyMergeDto } from '../models/CompanyMergeDto'
import type { CompanyTableItemDto } from '../models/CompanyTableItemDto'
import type { CreateCompanyDto } from '../models/CreateCompanyDto'
import type { CultureChangedEventData } from '../models/CultureChangedEventData'
import type { CultureResponseDto } from '../models/CultureResponseDto'
import type { DeliveryLocationDto } from '../models/DeliveryLocationDto'
import type { GetCompanyDto } from '../models/GetCompanyDto'
import type { GetCompanyDtoPaginated } from '../models/GetCompanyDtoPaginated'

export class MolsonPriceCmdCustomersApiService {
  /**
   * Method allows you to receive all customers
   * @returns CompanyTableItemDto OK
   * @throws ApiError
   */
  public static customerListTable(): CancelablePromise<
    Array<CompanyTableItemDto>
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/AllCustomersTable',
      errors: {
        404: `Not Found`
      }
    })
  }

  /**
   * Method allows you to receive customers page
   * @param page
   * @param size
   * @param companyName
   * @returns GetCompanyDtoPaginated OK
   * @throws ApiError
   */
  public static customersPage(
    page: number,
    size: number,
    companyName?: string
  ): CancelablePromise<GetCompanyDtoPaginated> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/AllCustomersPage',
      query: {
        page: page,
        size: size,
        companyName: companyName
      },
      errors: {
        404: `Not Found`
      }
    })
  }

  /**
   * Method allows you to receive existing Customer by id
   * @param id
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static getCustomerById(id: number): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/customer/{id}',
      path: {
        id: id
      },
      errors: {
        404: `Not Found`
      }
    })
  }

  /**
   * Method allows you to create new Customer
   * @param requestBody
   * @returns CreateCompanyDto Created
   * @throws ApiError
   */
  public static creatingNewCustomer(
    requestBody: CreateCompanyDto
  ): CancelablePromise<CreateCompanyDto> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/customer',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`
      }
    })
  }

  /**
   * Method allows you to count all customer duplicated records
   * @returns number OK
   * @throws ApiError
   */
  public static getAllDuplicatesCount(): CancelablePromise<number> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/customer/Duplicates/Count'
    })
  }

  /**
   * Method allows you to get duplicated records for a specific customer
   * @param id
   * @returns CompanyDuplicateDto OK
   * @throws ApiError
   */
  public static getDuplicates(
    id: number
  ): CancelablePromise<Array<CompanyDuplicateDto>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/customer/{id}/Duplicates',
      path: {
        id: id
      }
    })
  }

  /**
   * Method allows you to get all the PriceCMD customers, possibly marked as duplicate for the specified ERP customer
   * @param id
   * @returns CompanyDuplicateDto OK
   * @throws ApiError
   */
  public static getMarkedCustomerDuplicates(
    id: number
  ): CancelablePromise<Array<CompanyDuplicateDto>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/customer/{id}/MarkedDuplicates',
      path: {
        id: id
      }
    })
  }

  /**
   * Method allows you to get all the PriceCMD customers, possibly marked as duplicate for the specified ERP customer
   * @param id
   * @param requestBody
   * @returns any OK
   * @throws ApiError
   */
  public static mergeDuplicates(
    id: number,
    requestBody: CompanyMergeDto
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{id}/Merge',
      path: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`
      }
    })
  }

  /**
   * Method allows you to add delivery location to existing Customer
   * @param id
   * @param requestBody
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static addDeliveryLocationToCustomer(
    id: number,
    requestBody: DeliveryLocationDto
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/DeliveryLocation',
      query: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to update delivery location to existing Customer
   * @param id
   * @param requestBody
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static updateDeliveryLocationInCustomer(
    id: number,
    requestBody: DeliveryLocationDto
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/DeliveryLocation',
      query: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to delete delivery location to existing Customer
   * @param id
   * @param locationId
   * @param newLocationId
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static deleteDeliveryLocationFromCustomer(
    id: number,
    locationId: number,
    newLocationId?: number
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/DeliveryLocation',
      query: {
        id: id,
        locationId: locationId,
        newLocationId: newLocationId
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to hide delivery location for existing Customer
   * @param customerId
   * @param id
   * @param hide
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static hideDeliveryLocationForCustomer(
    customerId: number,
    id: number,
    hide: boolean
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/HideDeliveryLocation/{id}',
      path: {
        customerId: customerId,
        id: id
      },
      query: {
        hide: hide
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to set default delivery location to existing Customer
   * @param customerId
   * @param id
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static setDefaultDeliveryLocationForCustomer(
    customerId: number,
    id: number
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/SetDefaultDeliveryLocation/{id}',
      path: {
        customerId: customerId,
        id: id
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to update CompanyLocation`s [Hidden] property.
   * @param customerId
   * @param isHidden
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static hideCompanyOriginalAddress(
    customerId: number,
    isHidden: boolean
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/HideOriginalAddress',
      path: {
        customerId: customerId
      },
      query: {
        isHidden: isHidden
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to Add billing address to a company
   * @param requestBody
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static addBillingAddressToCompany(
    requestBody: CompanyLocationDto
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/BillingAddress',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to Update billing address to a company
   * @param id
   * @param requestBody
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static updateBillingAddressToCompany(
    id: number,
    requestBody: CompanyLocationDto
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/BillingAddress',
      query: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to Delete billing address from Customer
   * @param customerId
   * @param id
   * @param newAddressId
   * @returns void
   * @throws ApiError
   */
  public static deleteBillingAddressFromCustomer(
    customerId: number,
    id: number,
    newAddressId?: number
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/BillingAddress',
      query: {
        customerId: customerId,
        id: id,
        newAddressId: newAddressId
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to set default billing address for company
   * @param customerId
   * @param id
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static setDefaultBillingAddressForCompany(
    customerId: number,
    id: number
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/SetDefaultBillingAddress/{id}',
      path: {
        customerId: customerId,
        id: id
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to hide billing address for company
   * @param customerId
   * @param id
   * @param hide
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static hideBillingAddressForCompany(
    customerId: number,
    id: number,
    hide: boolean
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/HideBillingAddress/{id}',
      path: {
        customerId: customerId,
        id: id
      },
      query: {
        hide: hide
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to add contact details to existing Customer
   * @param id
   * @param requestBody
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static addContactDetailsToCustomer(
    id: number,
    requestBody: CompanyContactDto
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/AddContactDetails',
      query: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to update contact details to existing Customer
   * @param id
   * @param requestBody
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static updateContactDetailsInCustomer(
    id: number,
    requestBody: CompanyContactDto
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/UpdateContactDetails',
      query: {
        id: id
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to hide contact details for existing Customer
   * @param customerId
   * @param id
   * @param hide
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static hideContactDetailsForCustomer(
    customerId: number,
    id: number,
    hide: boolean
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/HideContact/{id}',
      path: {
        customerId: customerId,
        id: id
      },
      query: {
        hide: hide
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to set default contact details to existing Customer
   * @param customerId
   * @param id
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static setDefaultContactDetailsForCustomer(
    customerId: number,
    id: number
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/customer/{customerId}/SetDefaultContact/{id}',
      path: {
        customerId: customerId,
        id: id
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * Method allows you to delete contact details from existing Customer
   * @param companyId
   * @param contactId
   * @param newContactId
   * @returns GetCompanyDto OK
   * @throws ApiError
   */
  public static deleteContactDetailsFromCustomer(
    companyId: number,
    contactId: number,
    newContactId: number
  ): CancelablePromise<GetCompanyDto> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/DeleteContactDetails',
      query: {
        companyId: companyId,
        contactId: contactId,
        newContactId: newContactId
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        409: `Conflict`
      }
    })
  }

  /**
   * @param requestBody
   * @returns any OK
   * @throws ApiError
   */
  public static postInternalDeleteCustomersEvent(
    requestBody: Array<number>
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/Internal/delete_customers_event',
      body: requestBody,
      mediaType: 'application/json'
    })
  }

  /**
   * @param requestBody
   * @returns any OK
   * @throws ApiError
   */
  public static handleCultureChangedEventCustomersApi(
    requestBody: CultureChangedEventData
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/Internal/Culture/Changed',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`
      }
    })
  }

  /**
   * @returns CultureResponseDto OK
   * @throws ApiError
   */
  public static getCultureSettingsCustomersApi(): CancelablePromise<CultureResponseDto> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/Culture'
    })
  }

  /**
   * @returns string OK
   * @throws ApiError
   */
  public static getSupportedCulturesCustomersApi(): CancelablePromise<
    Array<string>
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/Culture/Supported'
    })
  }
}
